import React from "react";

/**
 * AqiStrip — air_quality evidence component.
 *
 * Five tiles, one per forecast day, coloured on the standard US EPA AQI scale.
 * Do not use the CampVantage rating scale here — people recognise EPA bands.
 *
 * Visual language follows IndexTileStrip: rounded-xl card shells with a subtle
 * tint, a 3px tone-coloured top border, ring, and a watermark icon — but the
 * accent colours are the EPA band colours (spec §0d), not the rating scale.
 *
 * Spec references: §0d (AQI bands), §7 (air_quality).
 */

/** Short date — "Aug 10" */
const shortDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Day of week — "Sun" */
const dayOfWeek = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "short" });
};

// ─── US EPA AQI bands (spec §0d) ──────────────────────────────────────────────
// Colours are the EPA standard. We use them as accents (border, ring, text,
// subtle tint) rather than solid fills so the strip reads cleaner.
const AQI_BANDS = [
  {
    max: 50,
    label: "Good",
    hex: "#00E400",
    bg: "bg-green-500/[0.06] dark:bg-green-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-[#00E400]",
    ring: "ring-1 ring-green-500/20 dark:ring-green-500/20",
    text: "text-green-600 dark:text-green-400",
    barFill: "bg-[#00E400]",
    watermark: "text-green-500/25 dark:text-green-500/15",
  },
  {
    max: 100,
    label: "Moderate",
    hex: "#FFFF00",
    bg: "bg-yellow-500/[0.06] dark:bg-yellow-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-[#FFFF00]",
    ring: "ring-1 ring-yellow-500/20 dark:ring-yellow-500/20",
    text: "text-yellow-600 dark:text-yellow-400",
    barFill: "bg-[#FFFF00]",
    watermark: "text-yellow-500/25 dark:text-yellow-500/15",
  },
  {
    max: 150,
    label: "Unhealthy for sensitive groups",
    hex: "#FF7E00",
    bg: "bg-orange-500/[0.06] dark:bg-orange-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-[#FF7E00]",
    ring: "ring-1 ring-orange-500/20 dark:ring-orange-500/20",
    text: "text-orange-600 dark:text-orange-400",
    barFill: "bg-[#FF7E00]",
    watermark: "text-orange-500/25 dark:text-orange-500/15",
  },
  {
    max: 200,
    label: "Unhealthy",
    hex: "#FF0000",
    bg: "bg-red-500/[0.06] dark:bg-red-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-[#FF0000]",
    ring: "ring-1 ring-red-500/20 dark:ring-red-500/20",
    text: "text-red-600 dark:text-red-400",
    barFill: "bg-[#FF0000]",
    watermark: "text-red-500/25 dark:text-red-500/15",
  },
  {
    max: 300,
    label: "Very unhealthy",
    hex: "#8F3F97",
    bg: "bg-purple-500/[0.06] dark:bg-purple-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-[#8F3F97]",
    ring: "ring-1 ring-purple-500/20 dark:ring-purple-500/20",
    text: "text-purple-600 dark:text-purple-400",
    barFill: "bg-[#8F3F97]",
    watermark: "text-purple-500/25 dark:text-purple-500/15",
  },
  {
    max: 9999,
    label: "Hazardous",
    hex: "#7E0023",
    bg: "bg-red-900/[0.06] dark:bg-red-900/[0.10]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-[#7E0023]",
    ring: "ring-1 ring-red-800/20 dark:ring-red-900/20",
    text: "text-red-700 dark:text-red-500",
    barFill: "bg-[#7E0023]",
    watermark: "text-red-800/25 dark:text-red-900/15",
  },
];

const getAqiBand = (aqi) =>
  AQI_BANDS.find((b) => aqi <= b.max) || AQI_BANDS[AQI_BANDS.length - 1];

// ─── Watermark icon per band ──────────────────────────────────────────────────
// A simple icon that reflects the severity level of each EPA band.
const BAND_ICONS = [
  // Good — leaf
  <path d="M5 21c0-9 7-16 16-16 0 9-7 16-16 16zM5 21l6-6" />,
  // Moderate — cloud-sun
  <>
    <circle cx="7" cy="8" r="3" />
    <path d="M7 2v1M2 8h1M12 8h1M3.5 4.5l.7.7M10.5 4.5l-.7.7M10 18a4 4 0 0 0 0-8 5 5 0 0 0-9.5 1.5A3.5 3.5 0 0 0 2 18h8z" />
  </>,
  // Unhealthy for sensitive — alert-triangle
  <path d="M12 3l9 16H3L12 3zM12 9v5M12 17h.01" />,
  // Unhealthy — mask
  <>
    <path d="M3 9a9 9 0 0 1 18 0v3a3 3 0 0 1-3 3h-2a2 2 0 0 1-2-2 2 2 0 0 1-2 2H6a3 3 0 0 1-3-3V9z" />
    <path d="M8 11h.01M16 11h.01" />
  </>,
  // Very unhealthy — skull
  <>
    <path d="M12 2a8 8 0 0 0-8 8v4a3 3 0 0 0 3 3h1v3h8v-3h1a3 3 0 0 0 3-3v-4a8 8 0 0 0-8-8z" />
    <circle cx="9" cy="11" r="1.5" />
    <circle cx="15" cy="11" r="1.5" />
  </>,
  // Hazardous — biohazard
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 9V5M12 15v4M9 12H5M15 12h4M7 7l2 2M17 7l-2 2M7 17l2-2M17 17l-2-2" />
  </>,
];

const AqiStrip = ({ panel }) => {
  const f = panel.facts || {};
  const days = f.days || [];

  if (days.length === 0) return null;

  const worstBand = f.worst_aqi != null ? getAqiBand(f.worst_aqi) : null;

  return (
    <div className="space-y-4">
      {/* ─── Worst AQI note ────────────────────────────────────────────── */}
      {f.worst_aqi != null && worstBand && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Worst AQI ahead:{" "}
          <span className={`font-semibold ${worstBand.text}`}>{f.worst_aqi}</span>{" "}
          — {worstBand.label}
        </p>
      )}

      {/* ─── Five tiles ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {days.map((day) => {
          const band = getAqiBand(day.aqi_max);
          const bandIndex = AQI_BANDS.indexOf(band);
          const icon = BAND_ICONS[bandIndex];

          return (
            <div
              key={day.date}
              className={`relative ${band.bg} rounded-xl shadow-sm border border-t-[3px] ${band.borderTop} ${band.border} ${band.ring} p-4 flex flex-col transition-shadow overflow-hidden`}
            >
              {/* Watermark icon */}
              <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                <div className={`absolute -top-4 -right-4 ${band.watermark}`}>
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {icon}
                  </svg>
                </div>
              </div>

              {/* Day + date — grouped together */}
              <p className="relative z-10 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {dayOfWeek(day.date)}, {shortDate(day.date)}
              </p>

              {/* AQI number + band label — grouped together */}
              <div className="relative z-10 flex items-baseline gap-1.5 mt-2">
                <p className={`text-3xl font-extrabold leading-none ${band.text}`}>
                  {day.aqi_max}
                </p>
                <p className={`text-[11px] font-semibold leading-tight ${band.text}`}>
                  {band.label}
                </p>
              </div>

              {/* PM2.5 and dust — side by side */}
              {(day.pm2_5_max != null || day.dust_max != null) && (
                <div className="relative z-10 mt-2 pt-2 border-t border-gray-100 dark:border-gray-800 flex justify-between">
                  {day.pm2_5_max != null && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500">
                      PM2.5: <span className="font-medium text-gray-600 dark:text-gray-300">{day.pm2_5_max}</span>
                    </p>
                  )}
                  {day.dust_max != null && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500">
                      Dust: <span className="font-medium text-gray-600 dark:text-gray-300">{day.dust_max}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AqiStrip;